/**
 * resolveBrandAsset — the single place tenant branding falls back.
 *
 * `TenantLogo`, `TenantIcon`, and `TenantBrand` are thin wrappers over this.
 * Before it existed each component owned its own chain, and two of the three
 * were surface-blind, which is how the dark canvas ended up special-cased
 * inside `TenantLogo` alone. One function means one place to read the rules and
 * one place a new surface has to be taught about.
 *
 * ## Chains
 *
 * `kind` maps 1:1 onto the three components, because they genuinely want three
 * different chains — that is why there are three components:
 *
 * ```
 * logo  (TenantLogo)   [dark: DARK_LOGO] -> LOGO          -> Butter full
 * icon  (TenantIcon)   ICON                               -> Butter icon
 * brand (TenantBrand)  ICON -> [dark: DARK_LOGO] -> LOGO  -> Butter full
 * ```
 *
 * Deliberate choices:
 *
 * - **A dark logo falls through to the regular one.** Correct for a mark on a
 *   solid plate or a light-inked badge — `virtualclubhousegolf` is canvas-ready
 *   with no dark entry at all. A dark variant is registered only when the
 *   regular mark actually vanishes on black.
 * - **`icon` does NOT fall through to the logo**, on either surface. That
 *   matches what `TenantIcon` has always done; widening it would silently start
 *   squeezing wordmarks into icon slots on every existing light surface.
 * - **There is no `TENANT_DARK_ICON_MAP`.** Nothing renders an icon on a dark
 *   surface. Add one when a surface needs it.
 *
 * The Butter fallback's `tone` flips with the surface. That was the actual bug
 * in the old chain: it always returned the light-ground wordmark, so an
 * unregistered tenant on the black canvas got dark ink on black.
 */

import { TENANT_DARK_LOGO_MAP, TENANT_ICON_MAP, TENANT_LOGO_MAP, getTenantConfig } from './registry';
import type { LogoComponent, TenantSurfaceConfig } from './registry';
import { logoSize, type LogoShape, type LogoSizeTier, type ResolvedLogoSize } from './sizing';

export type BrandSurface = 'light' | 'dark';

/** Which fallback chain to walk. Named for the component that wants it. */
export type BrandAssetKind = 'logo' | 'icon' | 'brand';

export interface ResolveBrandAssetArgs {
  /** Tenant handle. Unknown or missing resolves to the Butter fallback. */
  handle?: string;
  /** Which chain to walk. Defaults to `logo`. */
  kind?: BrandAssetKind;
  /** The ground it renders on. Defaults to `light`. */
  surface?: BrandSurface;
  /** Size tier for the context. Defaults to `lg`. */
  size?: LogoSizeTier;
}

export interface ResolvedBrandAsset {
  /**
   * The tenant's component, or `null` when nothing is registered and the caller
   * should render the Butter fallback. Returning `null` rather than the Butter
   * component keeps this module free of a `#primitives` import, so the chain is
   * unit-testable as plain data.
   */
  Logo: LogoComponent | null;
  /** Which Butter variant to render when `Logo` is null. */
  fallbackVariant: 'full' | 'icon';
  /** Butter fallback ink when `Logo` is null. A light ground wants dark ink. */
  tone: 'dark' | 'light';
  /**
   * Which layer of the chain won.
   *
   * Load-bearing, not informational: the `logoViewBox` crop describes the
   * *logo*, so it must never reach an icon — which is separate artwork with its
   * own viewBox. `BrandAsset` gates a caller-supplied `viewBox` on this for the
   * same reason the merge below withholds the registry one. Without it a
   * `TenantBrand` caller could hand a crop straight to a resolved icon and
   * reopen the hole.
   *
   * Also surfaced in the Playground, where "which layer won" is the answer to
   * "why is the crop control doing nothing on this panel".
   */
  layer: 'icon' | 'logo' | 'fallback';
  /** Shape that won — square when an icon resolved, else the registry's `logoSize`. */
  shape: LogoShape;
  /** Crop for this surface, already merged. Always absent when `layer` is `icon`. */
  viewBox?: string;
  /** Plate rounding for this surface, already merged. */
  rounded?: boolean;
  /** Tier sizing with this surface's `scale` applied. */
  sizing: ResolvedLogoSize;
}

const lookup = (map: Record<string, LogoComponent>, handle: string): LogoComponent | null =>
  Object.prototype.hasOwnProperty.call(map, handle) ? map[handle] : null;

interface Picked {
  Logo: LogoComponent | null;
  /** Set only when an ICON won — icons are square whatever the registry says. */
  iconWon: boolean;
}

function pickComponent(handle: string, kind: BrandAssetKind, surface: BrandSurface): Picked {
  const icon = lookup(TENANT_ICON_MAP, handle);
  const logo = lookup(TENANT_LOGO_MAP, handle);
  // The dark variant is only ever consulted on a dark ground.
  const darkLogo = surface === 'dark' ? lookup(TENANT_DARK_LOGO_MAP, handle) : null;

  if (kind === 'icon') return { Logo: icon, iconWon: !!icon };
  if (kind === 'brand' && icon) return { Logo: icon, iconWon: true };

  return { Logo: darkLogo ?? logo, iconWon: false };
}

/**
 * Resolve the component, presentation, and sizing for one tenant/kind/surface.
 *
 * Surface overrides are merged here so no call site has to know `dark.scale`
 * exists — components receive already-resolved values.
 */
export function resolveBrandAsset({
  handle,
  kind = 'logo',
  surface = 'light',
  size = 'lg',
}: ResolveBrandAssetArgs): ResolvedBrandAsset {
  const config = getTenantConfig(handle);
  const tone = surface === 'dark' ? 'light' : 'dark';
  const fallbackVariant = kind === 'icon' ? 'icon' : 'full';

  // `dark` is the only surface block today; light reads the flat/base fields.
  const overrides: TenantSurfaceConfig = (surface === 'dark' ? config.dark : undefined) ?? {};

  const picked = handle ? pickComponent(handle, kind, surface) : { Logo: null, iconWon: false };

  const layer: ResolvedBrandAsset['layer'] = picked.iconWon
    ? 'icon'
    : picked.Logo
      ? 'logo'
      : 'fallback';

  // Shape drives the height tier. An icon that won is square; a tenant logo
  // takes the registry's declared shape; the Butter fallback is a wide wordmark
  // except in an icon slot, where it's the square mark.
  const shape: LogoShape =
    layer === 'icon'
      ? 'square'
      : layer === 'logo'
        ? (config.logoSize ?? 'wide')
        : fallbackVariant === 'icon'
          ? 'square'
          : 'wide';

  return {
    Logo: picked.Logo,
    fallbackVariant,
    tone,
    layer,
    shape,
    // `logoViewBox` crops the *logo*; an icon is its own artwork with its own
    // viewBox, so the crop must not leak onto it. Both registered icons happen
    // to hardcode their viewBox and would ignore it today — this keeps that from
    // silently becoming a bug the first time someone retrofits one to accept the
    // prop, and matches what TenantIcon has always passed (nothing).
    viewBox: layer === 'icon' ? undefined : (overrides.viewBox ?? config.logoViewBox),
    rounded: overrides.rounded ?? config.roundedLogo,
    sizing: logoSize(size, shape, overrides.scale),
  };
}
